import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  FileText,
  Check,
  UserPlus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { Badge } from '@/components/ui/badge';
import type { TaskDetailsDialogProps } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';


export function TaskDetailsDialog({
  task,
  open,
  onOpenChange,
  isExternal,
  assigneeOptions = [],
  selectedAssigneeIds = [],
  onAssigneeToggle,
}: TaskDetailsDialogProps) {
  if (!task) return null;

  const priorityColor = (status: number) => {
    if (status === 3)
      return 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20';
    if (status === 2) return 'bg-sky-500/10 text-sky-600 border-sky-500/20';
    return 'bg-slate-500/10 text-slate-600 border-slate-500/20';
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-[600px] border-l border-border/40 bg-background/95 backdrop-blur-xl shadow-2xl p-0 overflow-hidden flex flex-col"
      >
        <SheetHeader className="p-6 pb-4 border-b">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn(
                    'px-2 py-0 h-5 text-[10px] font-semibold tracking-tight',
                    priorityColor(task.status)
                  )}
                >
                  {task.status === 3
                    ? 'Done'
                    : task.status === 2
                      ? 'Doing'
                      : 'Todo'}
                </Badge>
                <span className="text-xs text-muted-foreground font-medium">
                  #{task.id}
                </span>
              </div>
              <SheetTitle className="text-2xl font-bold tracking-tight bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent">
                {task.tasks}
              </SheetTitle>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 p-6 space-y-8 overflow-y-auto custom-scrollbar">
          {/* Description Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">
              <FileText className="w-4 h-4 text-primary" />
              <span>Description</span>
            </div>
            <div className="rounded-2xl border border-border/40 bg-muted/30 p-4 min-h-[100px] leading-relaxed">
              {task.task_description ? (
                <ReadOnlyContentDisplay html={task.task_description} />
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  No description provided.
                </p>
              )}
            </div>
          </div>

          {/* Task Info Grid */}
          <div className="grid grid-cols-2 gap-6 pt-4 border-t border-border/30">
            {!isExternal && (
              <div className="space-y-1">
                <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">Assignee</span>
                {assigneeOptions.length > 0 ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <div
                        className="flex w-full flex-wrap items-center -space-x-1"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {assigneeOptions.length > 0 && selectedAssigneeIds.length > 0 ? (
                          <>
                            {assigneeOptions
                              .filter((user) => selectedAssigneeIds.includes(user.id))
                              .slice(0, 3)
                              .map((user) => (
                                <Tooltip key={user.id}>
                                  <TooltipTrigger asChild>
                                    <Avatar className="h-7 w-7 border border-background ring-1 ring-border/20 -ml-1 first:ml-0 cursor-pointer transition-transform hover:scale-110">
                                      <AvatarImage src={user.photo_url || undefined} />
                                      <AvatarFallback className="text-[10px] bg-primary/70 text-primary font-bold">
                                        {user.name?.slice(0, 1)}
                                      </AvatarFallback>
                                    </Avatar>
                                  </TooltipTrigger>
                                  <TooltipContent side="top">{user.name}</TooltipContent>
                                </Tooltip>
                              ))}
                            {assigneeOptions.filter((user) => selectedAssigneeIds.includes(user.id)).length > 3 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Avatar className="h-7 w-7 border border-background ring-1 ring-border/20 -ml-1 cursor-pointer transition-transform hover:scale-110">
                                    <AvatarFallback className="bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-200 text-[10px] font-bold">
                                      +{assigneeOptions.filter((user) => selectedAssigneeIds.includes(user.id)).length - 3}
                                    </AvatarFallback>
                                  </Avatar>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  {assigneeOptions.filter((user) => selectedAssigneeIds.includes(user.id)).length - 3} more assignee{assigneeOptions.filter((user) => selectedAssigneeIds.includes(user.id)).length - 3 > 1 ? 's' : ''}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-8 justify-start gap-2 rounded-full border border-dashed border-primary/30 bg-primary/5 px-3 text-xs font-semibold text-primary hover:bg-primary/10"
                          >
                            <UserPlus className="h-3.5 w-3.5" />
                            Add assignee
                          </Button>
                        )}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      side="bottom"
                      sideOffset={10}
                      className="w-[min(92vw,320px)] p-0 overflow-hidden rounded-3xl border-border/60 bg-background shadow-2xl z-30"
                    >
                      <div className="border-b border-border/60 px-3 py-2">
                        <div className="text-xs font-semibold">Assignees</div>
                        <div className="text-[10px] text-muted-foreground">
                          Toggle users for this task
                        </div>
                      </div>
                      <Command>
                        <CommandInput placeholder="Search users..." className="h-10" />
                        <CommandList className="max-h-[280px]">
                          <CommandEmpty>No users found.</CommandEmpty>
                          <CommandGroup>
                            {assigneeOptions.map((user) => {
                              const selected = selectedAssigneeIds.includes(user.id);
                              return (
                                <CommandItem
                                  key={user.id}
                                  value={`${user.name} ${user.schema_name} ${user.email || ''}`}
                                  onSelect={() =>
                                    onAssigneeToggle?.({
                                      taskId: task.id,
                                      taskUuid: task.tasks_uuid,
                                      assignee: user,
                                      selected,
                                    })
                                  }
                                  className="flex items-center gap-3"
                                >
                                  <Avatar className="h-8 w-8">
                                    <AvatarImage src={user.photo_url || undefined} />
                                    <AvatarFallback className="text-[10px] bg-primary/70 text-white font-bold">
                                      {user.name?.slice(0, 1)}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium">{user.name}</p>
                                    <p className="truncate text-[11px] text-muted-foreground">
                                      {user.schema_name}
                                    </p>
                                  </div>
                                  {selected ? (
                                    <div className="h-4 w-4 rounded-full border border-emerald-500 bg-emerald-500/10 flex items-center justify-center">
                                      <Check className="h-3 w-3 text-emerald-500" />
                                    </div>
                                  ) : (
                                    <div className="h-4 w-4 rounded-full border border-border/60" />
                                  )}
                                </CommandItem>
                              );
                            })}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled
                    className="h-8 justify-start gap-2 rounded-full border border-dashed border-border/60 px-3 text-xs font-semibold text-muted-foreground"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    Add assignee
                  </Button>
                )}
              </div>
            )}
            <div className="space-y-1">
              <span className="text-[10px] font-semibold text-muted-foreground/70 uppercase tracking-tight ml-1">Estimated Time</span>
              <p className="text-sm font-medium">{task.estimated_hours || 'Not set'}</p>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
